import { useEffect, useState } from 'react';
import { TbX } from 'react-icons/tb';

import { ActionIcon, Box, Button, Group, Text } from '@mantine/core';

import { BannerType } from 'models';
import { digestString } from 'helpers/digestString';

import { useHeaderState } from 'components/header/hooks/use-header-state';
import { TextReplacer } from 'components/text-replacer/TextReplacer';
import { LocalStorageService } from 'services/LocalStorageService';

import { usePageLoadGlobals } from './use-page-load-globals';

const BANNER_MESSAGE_HASH_KEY = 'banner_message_hash';

export const useBannerMessage = (type: BannerType) => {
    const { showResourceProNav } = useHeaderState();

    const globals = usePageLoadGlobals();

    const { message, type: typeToDisplay, button: { link, label } = {} } = globals?.bannerMessage || {};

    const [bannerDismissed, setBannerDismissed] = useState(true);
    const storedHash = LocalStorageService.get(BANNER_MESSAGE_HASH_KEY);

    const isEditorPage = typeof window !== 'undefined' && window.location.pathname.endsWith('/editor');

    useEffect(() => {
        if (!message) {
            return;
        }

        digestString(message).then((hash) => {
            setBannerDismissed(hash === storedHash);
        });
    }, [message, storedHash]);

    const dismissBanner = () => {
        setBannerDismissed(true);

        digestString(message!).then((hash) => {
            LocalStorageService.store(BANNER_MESSAGE_HASH_KEY, hash);
        });
    };

    const renderMessage = () => {
        if (!message) return null;

        const boldPattern = /\*\*(.*?)\*\*/g;
        const matches = [...message.matchAll(boldPattern)];

        if (!matches.length) {
            return <Text fw={600}>{message}</Text>;
        }

        const replaces: Record<string, React.ReactNode> = {};
        matches.forEach((match) => {
            replaces[match[0]] = (
                <TextReplacer.Gradient key={`${match[0]}-${match.index}`}>{match[1]}</TextReplacer.Gradient>
            );
        });

        return (
            <Text fw={500}>
                <TextReplacer replaces={replaces}>{message}</TextReplacer>
            </Text>
        );
    };

    const BannerContent = (
        <>
            <Group gap="xs" align="center" justify="space-between" w="100%">
                {renderMessage()}
                {link ? (
                    <Box
                        className="animation-border-spin"
                        style={{
                            '--background-color': 'var(--mantine-color-green-1)',
                            '--animation-color': 'var(--mantine-color-cyan-2)',
                            '--border-width': '3px',
                            '--animation-duration': '2s',
                            borderRadius: '7px',
                        }}
                    >
                        <Button
                            component="a"
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="xs"
                            onClick={dismissBanner}
                        >
                            {label || 'Learn more'}
                        </Button>
                    </Box>
                ) : null}
            </Group>

            <ActionIcon onClick={dismissBanner} size="sm" variant="transparent" aria-label="Dismiss banner" ml="xs">
                <TbX />
            </ActionIcon>
        </>
    );

    return {
        BannerContent,
        showBanner: Boolean(
            !bannerDismissed && message && typeToDisplay === type && !showResourceProNav && !isEditorPage,
        ),
    };
};
